import { displayRfqId } from './rfqIdentity';

/**
 * What the buyer just did, kept for Robbie. Steps record an action in the
 * buyer's own words ("Kept 10 of 13 lines in the credit gate (3 left the
 * BOM)"); the chat sends the last few with every message, so "why did 3 lines
 * disappear?" is answered from what happened rather than guessed. The server
 * adds its own record of the BOM next to this list, so a reload or another
 * device is still covered.
 *
 * One buffer per RFQ, mirrored to `sessionStorage` so a reload within the tab
 * keeps it. Storage is a convenience: when it is unavailable the buffer
 * simply lives in memory. Signing out clears every buffer
 * (`clearRobbieActivity`), so a shared device never hands one buyer's actions
 * to the next buyer who opens the same RFQ.
 */

/** Mirrors buyer-service's allow-list (`services/chat/recentActions.js`). */
export type RobbieActionKind =
  | 'upload'
  | 'extract'
  | 'keepLines'
  | 'editBom'
  | 'saveCriteria'
  | 'startDiscovery'
  | 'proposalApplied'
  | 'proposalDismissed'
  | 'navigate'
  | 'chatAsk';

export interface RobbieAction {
  /** ISO timestamp of when it happened. */
  at: string;
  kind: RobbieActionKind;
  /** One line, in the buyer's words, with the real numbers. */
  text: string;
}

/** The server keeps the newest 12 as well, and cuts the text at 160. */
export const MAX_ROBBIE_ACTIONS = 12;
export const MAX_ROBBIE_ACTION_TEXT = 160;

const STORAGE_PREFIX = 'hr.robbie.recentActions.';

/** Display form, so `RFQ-003` and `RFQ-003-A7K2` share one buffer. */
const storageKey = (rfqId: string) => STORAGE_PREFIX + displayRfqId(rfqId);

const isAction = (value: unknown): value is RobbieAction => {
  const candidate = value as Partial<RobbieAction> | null;
  return (
    !!candidate &&
    typeof candidate.at === 'string' &&
    typeof candidate.kind === 'string' &&
    typeof candidate.text === 'string'
  );
};

/** Newest last, bounded: the one place the ring-buffer rule lives. */
export const appendRobbieAction = (actions: RobbieAction[], action: RobbieAction): RobbieAction[] =>
  [...actions, action].slice(-MAX_ROBBIE_ACTIONS);

export const readRobbieActions = (rfqId: string): RobbieAction[] => {
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(storageKey(rfqId)) || '[]');
    return Array.isArray(parsed) ? parsed.filter(isAction).slice(-MAX_ROBBIE_ACTIONS) : [];
  } catch {
    // Unreadable storage is the same as an empty buffer.
    return [];
  }
};

const writeRobbieActions = (rfqId: string, actions: RobbieAction[]) => {
  try {
    window.sessionStorage.setItem(storageKey(rfqId), JSON.stringify(actions));
  } catch {
    // Private mode or a full quota: the buffer still serves this page load.
  }
};

/** Sign-out: drop every RFQ's buffer from this tab's storage. */
export const clearRobbieActivity = () => {
  try {
    const storage = window.sessionStorage;
    // Collect first: removing while indexing would skip keys.
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key && key.startsWith(STORAGE_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => storage.removeItem(key));
  } catch {
    // Storage unavailable: nothing was persisted, so there is nothing to clear.
  }
};

export interface RobbieActivity {
  /** Add one action, newest last; blank text is ignored. */
  record: (kind: RobbieActionKind, text: string) => void;
  /** The buffer, oldest first (a copy). */
  recent: () => RobbieAction[];
}

/**
 * One RFQ's buffer. Held in a closure rather than React state on purpose:
 * nothing renders it, and recording an action from a step must not re-render
 * the wizard tree.
 */
export const createRobbieActivity = (rfqId: string, now: () => Date = () => new Date()): RobbieActivity => {
  let actions = readRobbieActions(rfqId);
  return {
    record: (kind, text) => {
      const line = text.replace(/\s+/g, ' ').trim().slice(0, MAX_ROBBIE_ACTION_TEXT);
      if (!line) return;
      actions = appendRobbieAction(actions, { at: now().toISOString(), kind, text: line });
      writeRobbieActions(rfqId, actions);
    },
    recent: () => [...actions],
  };
};

const plural = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`;

/** The same sentences each site records; kept here so they read the same everywhere and are tested once. */
export const robbieActionText = {
  uploaded: (names: string[]) =>
    names.length === 1
      ? `Uploaded ${names[0]}`
      : `Uploaded ${names.length} files: ${names.slice(0, 3).join(', ')}${names.length > 3 ? `, +${names.length - 3} more` : ''}`,
  extracted: (lines: number, needAttention: number) =>
    `Extracted ${plural(lines, 'line')} from the upload${needAttention > 0 ? `, ${needAttention} need attention` : ''}`,
  keptLines: (kept: number, removed: number) =>
    removed === 0
      ? `Kept all ${plural(kept, 'line')} in the credit gate`
      : `Kept ${kept} of ${kept + removed} lines in the credit gate (${removed} left the BOM)`,
  editedBom: (changed: number, added: number, removed: number) =>
    `Edited the BOM: ${[
      changed > 0 && `${plural(changed, 'line')} changed`,
      added > 0 && `${added} added`,
      removed > 0 && `${removed} removed`,
    ]
      .filter(Boolean)
      .join(', ')}`,
  savedCriteria: (leadTime: string, complianceCount: number) =>
    `Saved sourcing criteria (lead time ${leadTime.replace('-', '–')}, ${
      complianceCount === 0 ? 'no compliance standards' : plural(complianceCount, 'compliance standard')
    })`,
  startedDiscovery: (lines: number, credits: number | null, leftOut: number) =>
    `Started supplier discovery for ${plural(lines, 'line')}${
      credits === null ? ' · nothing new to pay for' : ` · ${credits.toLocaleString('en-IN')} credits`
    }${leftOut > 0 ? ` (${leftOut} left the BOM)` : ''}`,
  appliedProposal: (changes: number, lines: number) =>
    `Applied Robbie's proposal: ${plural(changes, 'change')} on ${plural(lines, 'line')}`,
  proposalUnchanged: () => "Tried to apply Robbie's proposal, but the BOM already had those values, so nothing changed",
  dismissedProposal: () => "Dismissed Robbie's proposal",
  appliedStepProposal: (titles: string[]) =>
    `Applied Robbie's proposal for ${titles.slice(0, 3).join(', ')}${titles.length > 3 ? ` and ${titles.length - 3} more` : ''}`,
  wentToStep: (step: number, title: string) => `Went to step ${step} (${title})`,
  askedToReview: (selected: number | null, total: number) =>
    selected === null
      ? `Asked Robbie to review all ${plural(total, 'line')}`
      : `Asked Robbie to review ${selected} selected ${selected === 1 ? 'line' : 'lines'}`,
};
